import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Filter, X, ChevronDown, ChevronUp } from "lucide-react";
import { api } from "@/api/client";
import type {
  JenisPNBP,
  SiklusKeuangan,
  BentukDokumen,
  Dimensi,
  Tag,
  SearchFilters as SearchFiltersType,
  ConfidentialityLevel,
} from "@/types/document";

interface SearchFiltersProps {
  value: SearchFiltersType;
  onChange: (filters: SearchFiltersType) => void;
  onReset: () => void;
}

const CONFIDENTIALITY_OPTIONS: { value: ConfidentialityLevel; label: string }[] = [
  { value: "PUBLIC", label: "Publik" },
  { value: "INTERNAL", label: "Internal" },
  { value: "CONFIDENTIAL", label: "Rahasia" },
  { value: "SECRET", label: "Sangat Rahasia" },
];

function FilterSection({
  title,
  children,
  defaultOpen = true,
}: {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-gray-200 pb-4">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between py-2 text-sm font-medium text-gray-700"
      >
        {title}
        {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>
      {open && <div className="mt-2 space-y-1">{children}</div>}
    </div>
  );
}

function CheckboxItem({
  id,
  label,
  checked,
  onChange,
}: {
  id: string;
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 rounded px-1 py-0.5">
      <input
        type="checkbox"
        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="text-sm text-gray-700">{label}</span>
    </label>
  );
}

export default function SearchFilters({ value, onChange, onReset }: SearchFiltersProps) {
  const [freeTagInput, setFreeTagInput] = useState("");

  const { data: jenisList = [] } = useQuery<JenisPNBP[]>({
    queryKey: ["master", "jenis-pnbp"],
    queryFn: () => api.get("/master/jenis-pnbp"),
  });

  const { data: siklusList = [] } = useQuery<SiklusKeuangan[]>({
    queryKey: ["master", "siklus-keuangan"],
    queryFn: () => api.get("/master/siklus-keuangan"),
  });

  const { data: bentukList = [] } = useQuery<BentukDokumen[]>({
    queryKey: ["master", "bentuk-dokumen"],
    queryFn: () => api.get("/master/bentuk-dokumen"),
  });

  const { data: dimensiList = [] } = useQuery<Dimensi[]>({
    queryKey: ["master", "dimensi"],
    queryFn: () => api.get("/master/dimensi"),
  });

  const { data: tagList = [] } = useQuery<Tag[]>({
    queryKey: ["tags"],
    queryFn: () => api.get("/tags/"),
  });

  // Generic toggle helper for array filters
  function toggleArrayFilter<T extends string>(
    key: keyof SearchFiltersType,
    id: T,
    checked: boolean
  ) {
    const current = (value[key] as T[] | undefined) ?? [];
    onChange({
      ...value,
      [key]: checked ? [...current, id] : current.filter((x) => x !== id),
    });
  }

  function addFreeTag() {
    const trimmed = freeTagInput.trim().toLowerCase();
    if (!trimmed) return;
    const existing = value.free_tags ?? [];
    if (!existing.includes(trimmed)) {
      onChange({ ...value, free_tags: [...existing, trimmed] });
    }
    setFreeTagInput("");
  }

  function removeFreeTag(tag: string) {
    onChange({ ...value, free_tags: (value.free_tags ?? []).filter((t) => t !== tag) });
  }

  const hasActiveFilters =
    (value.jenis_pnbp_ids?.length ?? 0) > 0 ||
    (value.siklus_ids?.length ?? 0) > 0 ||
    (value.bentuk_ids?.length ?? 0) > 0 ||
    (value.dimensi_ids?.length ?? 0) > 0 ||
    (value.tag_ids?.length ?? 0) > 0 ||
    (value.free_tags?.length ?? 0) > 0 ||
    (value.confidentiality?.length ?? 0) > 0 ||
    value.date_range?.start ||
    value.date_range?.end;

  return (
    <aside className="w-full md:w-72 md:shrink-0 rounded-xl border border-gray-200 bg-white p-4 shadow-sm space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-500" />
          <h3 className="text-sm font-semibold text-gray-800">Filter Pencarian</h3>
        </div>
        {hasActiveFilters && (
          <button
            type="button"
            onClick={onReset}
            className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700"
          >
            <X className="h-3 w-3" />
            Reset
          </button>
        )}
      </div>

      {/* Jenis PNBP */}
      <FilterSection title="Jenis PNBP">
        {jenisList.map((j) => (
          <CheckboxItem
            key={j.id}
            id={j.id}
            label={`${j.kode} — ${j.nama}`}
            checked={(value.jenis_pnbp_ids ?? []).includes(j.id)}
            onChange={(checked) => toggleArrayFilter("jenis_pnbp_ids", j.id, checked)}
          />
        ))}
      </FilterSection>

      {/* Siklus Keuangan */}
      <FilterSection title="Siklus Keuangan">
        {siklusList.map((s) => (
          <CheckboxItem
            key={s.id}
            id={s.id}
            label={`${s.kode} — ${s.nama}`}
            checked={(value.siklus_ids ?? []).includes(s.id)}
            onChange={(checked) => toggleArrayFilter("siklus_ids", s.id, checked)}
          />
        ))}
      </FilterSection>

      {/* Bentuk Dokumen */}
      <FilterSection title="Bentuk Dokumen" defaultOpen={false}>
        {bentukList.map((b) => (
          <CheckboxItem
            key={b.id}
            id={b.id}
            label={`${b.kode} — ${b.nama}`}
            checked={(value.bentuk_ids ?? []).includes(b.id)}
            onChange={(checked) => toggleArrayFilter("bentuk_ids", b.id, checked)}
          />
        ))}
      </FilterSection>

      {/* Dimensi */}
      <FilterSection title="Dimensi Fungsional" defaultOpen={false}>
        {dimensiList.map((d) => (
          <CheckboxItem
            key={d.id}
            id={d.id}
            label={`${d.kode} — ${d.nama}`}
            checked={(value.dimensi_ids ?? []).includes(d.id)}
            onChange={(checked) => toggleArrayFilter("dimensi_ids", d.id, checked)}
          />
        ))}
      </FilterSection>

      {/* Tags */}
      <FilterSection title="Tag" defaultOpen={false}>
        <div className="flex flex-wrap gap-1 mb-2">
          {tagList.map((tag) => {
            const active = (value.tag_ids ?? []).includes(tag.id);
            return (
              <button
                key={tag.id}
                type="button"
                onClick={() => toggleArrayFilter("tag_ids", tag.id, !active)}
                className={`rounded-full px-2 py-0.5 text-xs font-medium transition-colors ${
                  active
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {tag.nama}
              </button>
            );
          })}
        </div>

        {/* Free tag input */}
        <div className="flex gap-1">
          <input
            value={freeTagInput}
            onChange={(e) => setFreeTagInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addFreeTag();
              }
            }}
            className="flex-1 rounded border border-gray-300 px-2 py-1 text-xs"
            placeholder="Tag bebas..."
          />
          <button
            type="button"
            onClick={addFreeTag}
            className="rounded bg-gray-100 px-2 py-1 text-xs hover:bg-gray-200"
          >
            +
          </button>
        </div>

        {(value.free_tags ?? []).length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {(value.free_tags ?? []).map((tag) => (
              <span
                key={tag}
                className="flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-800"
              >
                {tag}
                <button type="button" onClick={() => removeFreeTag(tag)}>
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </FilterSection>

      {/* Tingkat Kerahasiaan */}
      <FilterSection title="Tingkat Kerahasiaan" defaultOpen={false}>
        {CONFIDENTIALITY_OPTIONS.map((opt) => (
          <CheckboxItem
            key={opt.value}
            id={opt.value}
            label={opt.label}
            checked={(value.confidentiality ?? []).includes(opt.value)}
            onChange={(checked) =>
              toggleArrayFilter("confidentiality", opt.value, checked)
            }
          />
        ))}
      </FilterSection>

      {/* Date Range */}
      <FilterSection title="Tanggal Dokumen" defaultOpen={false}>
        <div className="space-y-2">
          <div>
            <label className="text-xs text-gray-500">Dari</label>
            <input
              type="date"
              className="w-full rounded border border-gray-300 px-2 py-1 text-xs"
              value={value.date_range?.start ?? ""}
              onChange={(e) =>
                onChange({
                  ...value,
                  date_range: { ...value.date_range, start: e.target.value },
                })
              }
            />
          </div>
          <div>
            <label className="text-xs text-gray-500">Sampai</label>
            <input
              type="date"
              className="w-full rounded border border-gray-300 px-2 py-1 text-xs"
              value={value.date_range?.end ?? ""}
              onChange={(e) =>
                onChange({
                  ...value,
                  date_range: { ...value.date_range, end: e.target.value },
                })
              }
            />
          </div>
        </div>
      </FilterSection>
    </aside>
  );
}
